import { createAsyncThunk, createSlice, SerializedError } from '@reduxjs/toolkit';
import { RequestStatusObject, createRequestStatusObject, buildReducers } from '../utils';
import { API } from 'aws-amplify';
import { RootState, AppDispatch } from '../store';
import { enqueueSnackbar } from 'notistack';
import i18n from '../../i18n';

/**
 * Detects errors caused by the user's session being torn down (e.g. during
 * logout) rather than a genuine load failure. When the session is gone,
 * in-flight admin requests reject with a 401/403 or a "no current user" error.
 * Surfacing an error toast in that case is misleading because the user is
 * already being redirected to login.
 */
const isAuthTeardownError = (error?: SerializedError): boolean => {
	if (!error) return false;
	const haystack = `${error.name ?? ''} ${error.message ?? ''} ${error.code ?? ''}`.toLowerCase();
	return (
		haystack.includes('401') ||
		haystack.includes('403') ||
		haystack.includes('unauthorized') ||
		haystack.includes('forbidden') ||
		haystack.includes('no current user') ||
		haystack.includes('not authenticated') ||
		haystack.includes('no credentials')
	);
};

// ─── Types ───────────────────────────────────────────────────────────────────

export interface KbFile {
	key: string;
	size: number;
	lastModified: string;
	etag: string;
}

export interface PromptGroupsMap {
	identity?: string | null;
	tone?: string | null;
	scope?: string | null;
	operational?: string | null;
	withContext?: string | null;
	noContext?: string | null;
}

export interface KbConfig {
	knowledgeBaseId: string;
	name: string;
	region: string;
	modelArn: string;
	sourceBucket: string;
	dataSourceId?: string;
	guardrailId?: string | null;
	guardrailVersion?: string | null;
	status: 'active' | 'disabled';
	temperature?: number;
	maxTokens?: number;
	minRelevanceScore?: number;
	numberOfResults?: number;
	systemPrompt?: string;
	withContextInstructions?: string;
	noContextInstructions?: string;
	prompts?: PromptGroupsMap | null;
	promptDefaults?: PromptGroupsMap;
	cacheEnabled?: boolean;
	cacheTtlSeconds?: number;
	webSearchEnabled?: boolean;
	createdAt?: string;
	updatedAt?: string;
}

export type AgentType = 'chatAgent' | 'documentRequestAgent';

export interface AgentBinding {
	agentType: AgentType;
	knowledgeBaseId: string;
	description: string;
	status: 'active' | 'disabled';
	createdAt: string;
	updatedAt?: string;
}

export interface SyncJob {
	ingestionJobId: string;
	status: string;
	startedAt: string;
	updatedAt?: string;
	statistics?: Record<string, number>;
}

export interface GuardrailSummary {
	guardrailId: string;
	guardrailArn: string;
	name: string;
	description: string;
	status: string;
	version: string;
	createdAt?: string;
	updatedAt?: string;
}

export interface AllowedModel {
	arn: string;
	label: string;
	region: string;
}

export interface GuardrailDetail extends GuardrailSummary {
	blockedInputMessaging?: string;
	blockedOutputsMessaging?: string;
	topicPolicy?: { topics: { name: string; definition: string; type: string }[] };
	contentPolicy?: { filters: { type: string; inputStrength: string; outputStrength: string }[] };
	wordPolicy?: { managedWordLists?: { type: string }[]; words?: { text: string }[] };
	sensitiveInformationPolicy?: {
		piiEntities?: { type: string; action: string }[];
		regexes?: { name: string; pattern: string; action: string }[];
	};
	contextualGroundingPolicy?: { filters: { type: string; threshold: number }[] };
}

// ─── State ───────────────────────────────────────────────────────────────────

export interface KbAdminState {
	configs: KbConfig[];
	selectedKbId: string | null;
	selectedConfig: KbConfig | null;
	files: KbFile[];
	filesTotalCount: number;
	nextContinuationToken: string | null;
	syncJobs: SyncJob[];
	latestSyncJob: SyncJob | null;
	agents: AgentBinding[];
	guardrails: GuardrailSummary[];
	selectedGuardrail: GuardrailDetail | null;
	allowedModels: AllowedModel[];

	listConfigsRequest: RequestStatusObject;
	getConfigRequest: RequestStatusObject;
	updateConfigRequest: RequestStatusObject;
	updateConfigStatusRequest: RequestStatusObject;
	listFilesRequest: RequestStatusObject;
	uploadFileRequest: RequestStatusObject;
	deleteFileRequest: RequestStatusObject;
	triggerSyncRequest: RequestStatusObject;
	syncStatusRequest: RequestStatusObject;
	listAgentsRequest: RequestStatusObject;
	updateAgentRequest: RequestStatusObject;
	updateAgentStatusRequest: RequestStatusObject;
	listGuardrailsRequest: RequestStatusObject;
	getGuardrailRequest: RequestStatusObject;
	listAllowedModelsRequest: RequestStatusObject;
}

const initialState: KbAdminState = {
	configs: [],
	selectedKbId: null,
	selectedConfig: null,
	files: [],
	filesTotalCount: 0,
	nextContinuationToken: null,
	syncJobs: [],
	latestSyncJob: null,
	agents: [],
	guardrails: [],
	selectedGuardrail: null,
	allowedModels: [],

	listConfigsRequest: createRequestStatusObject(),
	getConfigRequest: createRequestStatusObject(),
	updateConfigRequest: createRequestStatusObject(),
	updateConfigStatusRequest: createRequestStatusObject(),
	listFilesRequest: createRequestStatusObject(),
	uploadFileRequest: createRequestStatusObject(),
	deleteFileRequest: createRequestStatusObject(),
	triggerSyncRequest: createRequestStatusObject(),
	syncStatusRequest: createRequestStatusObject(),
	listAgentsRequest: createRequestStatusObject(),
	updateAgentRequest: createRequestStatusObject(),
	updateAgentStatusRequest: createRequestStatusObject(),
	listGuardrailsRequest: createRequestStatusObject(),
	getGuardrailRequest: createRequestStatusObject(),
	listAllowedModelsRequest: createRequestStatusObject()
};

// ─── Thunks ──────────────────────────────────────────────────────────────────

export const listKbConfigs = createAsyncThunk<
	{ configs: KbConfig[] },
	void,
	{ dispatch: AppDispatch; state: RootState }
>('kbAdmin/listConfigs', async () => {
	const response = await API.get('inspirusapi', '/kb-admin/configs', {
		headers: {},
		response: true
	});
	return response.data;
});

export const listAllowedModels = createAsyncThunk<
	{ models: AllowedModel[] },
	void,
	{ dispatch: AppDispatch; state: RootState }
>('kbAdmin/listAllowedModels', async () => {
	const response = await API.get('inspirusapi', '/kb-admin/configs/allowed-models', {
		headers: {},
		response: true
	});
	return response.data;
});

export const listKbFiles = createAsyncThunk<
	{ files: KbFile[]; totalCount: number; nextContinuationToken: string | null },
	{ knowledgeBaseId: string; continuationToken?: string | null },
	{ dispatch: AppDispatch; state: RootState }
>('kbAdmin/listFiles', async ({ knowledgeBaseId, continuationToken }) => {
	const response = await API.get('inspirusapi', '/kb-admin/files', {
		headers: {},
		response: true,
		queryStringParameters: {
			knowledgeBaseId,
			maxKeys: '100',
			...(continuationToken ? { continuationToken } : {})
		}
	});
	return response.data;
});

export const getPresignedUploadUrl = createAsyncThunk<
	{ uploadUrl: string; fileKey: string },
	{ knowledgeBaseId: string; fileKey: string; contentType: string },
	{ dispatch: AppDispatch; state: RootState }
>('kbAdmin/getPresignedUploadUrl', async ({ knowledgeBaseId, fileKey, contentType }) => {
	const response = await API.post('inspirusapi', '/kb-admin/files/presign', {
		headers: {},
		response: true,
		body: { knowledgeBaseId, fileKey, contentType }
	});
	return response.data;
});

export const uploadKbFile = createAsyncThunk<
	{ uploaded: boolean; fileKey: string; size: number },
	{ knowledgeBaseId: string; fileKey: string; contentType: string; fileContent: string },
	{ dispatch: AppDispatch; state: RootState }
>('kbAdmin/uploadFile', async ({ knowledgeBaseId, fileKey, contentType, fileContent }) => {
	const response = await API.post('inspirusapi', '/kb-admin/files/upload', {
		headers: {},
		response: true,
		body: { knowledgeBaseId, fileKey, contentType, fileContent }
	});
	return response.data;
});

export const deleteKbFile = createAsyncThunk<
	{ deleted: boolean; fileKey: string },
	{ knowledgeBaseId: string; fileKey: string },
	{ dispatch: AppDispatch; state: RootState }
>('kbAdmin/deleteFile', async ({ knowledgeBaseId, fileKey }) => {
	const response = await API.del('inspirusapi', `/kb-admin/files/${fileKey}`, {
		headers: {},
		queryStringParameters: { knowledgeBaseId }
	});
	return response;
});

export const triggerSync = createAsyncThunk<
	SyncJob,
	{ knowledgeBaseId: string },
	{ dispatch: AppDispatch; state: RootState }
>('kbAdmin/triggerSync', async ({ knowledgeBaseId }) => {
	const response = await API.post('inspirusapi', '/kb-admin/sync', {
		headers: {},
		response: true,
		body: { knowledgeBaseId }
	});
	return response.data;
});

export const getSyncStatus = createAsyncThunk<
	{ jobs: SyncJob[] },
	{ knowledgeBaseId: string },
	{ dispatch: AppDispatch; state: RootState }
>('kbAdmin/getSyncStatus', async ({ knowledgeBaseId }) => {
	const response = await API.get('inspirusapi', '/kb-admin/sync/status', {
		headers: {},
		response: true,
		queryStringParameters: { knowledgeBaseId }
	});
	return response.data;
});

// ─── Config Thunks ───────────────────────────────────────────────────────────

export const getKbConfig = createAsyncThunk<
	KbConfig,
	{ knowledgeBaseId: string },
	{ dispatch: AppDispatch; state: RootState }
>('kbAdmin/getConfig', async ({ knowledgeBaseId }) => {
	const response = await API.get('inspirusapi', `/kb-admin/configs/${knowledgeBaseId}`, {
		headers: {},
		response: true
	});
	return response.data;
});

export const updateKbConfig = createAsyncThunk<
	KbConfig,
	{ knowledgeBaseId: string; updates: Partial<Omit<KbConfig, 'knowledgeBaseId' | 'createdAt' | 'updatedAt'>> },
	{ dispatch: AppDispatch; state: RootState }
>('kbAdmin/updateConfig', async ({ knowledgeBaseId, updates }) => {
	const response = await API.put('inspirusapi', `/kb-admin/configs/${knowledgeBaseId}`, {
		headers: {},
		response: true,
		body: updates
	});
	return response.data;
});

export const updateKbConfigStatus = createAsyncThunk<
	{ knowledgeBaseId: string; status: 'active' | 'disabled' },
	{ knowledgeBaseId: string; status: 'active' | 'disabled' },
	{ dispatch: AppDispatch; state: RootState }
>('kbAdmin/updateConfigStatus', async ({ knowledgeBaseId, status }) => {
	const response = await API.put('inspirusapi', `/kb-admin/configs/${knowledgeBaseId}/status`, {
		headers: {},
		response: true,
		body: { status }
	});
	return response.data;
});

// ─── Agent Thunks ────────────────────────────────────────────────────────────

export const listAgents = createAsyncThunk<
	{ agents: AgentBinding[] },
	void,
	{ dispatch: AppDispatch; state: RootState }
>('kbAdmin/listAgents', async () => {
	const response = await API.get('inspirusapi', '/kb-admin/agents', {
		headers: {},
		response: true
	});
	return response.data;
});

export const updateAgent = createAsyncThunk<
	AgentBinding,
	{ agentType: AgentType; updates: { knowledgeBaseId?: string; description?: string } },
	{ dispatch: AppDispatch; state: RootState }
>('kbAdmin/updateAgent', async ({ agentType, updates }) => {
	const response = await API.put('inspirusapi', `/kb-admin/agents/${agentType}`, {
		headers: {},
		response: true,
		body: updates
	});
	return response.data;
});

export const updateAgentStatus = createAsyncThunk<
	{ agentType: AgentType; status: 'active' | 'disabled' },
	{ agentType: AgentType; status: 'active' | 'disabled' },
	{ dispatch: AppDispatch; state: RootState }
>('kbAdmin/updateAgentStatus', async ({ agentType, status }) => {
	const response = await API.put('inspirusapi', `/kb-admin/agents/${agentType}/status`, {
		headers: {},
		response: true,
		body: { status }
	});
	return response.data;
});

// ─── Guardrail Thunks ────────────────────────────────────────────────────────

export const listGuardrails = createAsyncThunk<
	{ guardrails: GuardrailSummary[] },
	void,
	{ dispatch: AppDispatch; state: RootState }
>('kbAdmin/listGuardrails', async () => {
	const response = await API.get('inspirusapi', '/kb-admin/guardrails', {
		headers: {},
		response: true
	});
	return response.data;
});

export const getGuardrailDetail = createAsyncThunk<
	GuardrailDetail,
	{ guardrailId: string; version?: string },
	{ dispatch: AppDispatch; state: RootState }
>('kbAdmin/getGuardrailDetail', async ({ guardrailId, version }) => {
	const queryString = version ? `?version=${version}` : '';
	const response = await API.get('inspirusapi', `/kb-admin/guardrails/${guardrailId}${queryString}`, {
		headers: {},
		response: true
	});
	return response.data;
});

// ─── Slice ───────────────────────────────────────────────────────────────────

const kbAdminSlice = createSlice({
	name: 'kbAdmin',
	initialState,
	reducers: {
		resetKbAdmin() {
			return initialState;
		},
		setSelectedKbId(state, action) {
			state.selectedKbId = action.payload;
			// Reset file/sync state so the new KB's data is fetched
			state.files = [];
			state.filesTotalCount = 0;
			state.nextContinuationToken = null;
			state.latestSyncJob = null;
			state.listFilesRequest = createRequestStatusObject();
			state.syncStatusRequest = createRequestStatusObject();
		}
	},
	extraReducers: (builder) => {
		buildReducers(builder, listKbConfigs, 'listConfigsRequest', {
			onFulfilled: (state, payload) => {
				state.configs = payload.configs;
				if (!state.selectedKbId && payload.configs.length > 0) {
					state.selectedKbId = payload.configs[0].knowledgeBaseId;
				}
			},
			onRejected: (_state, error) => {
				if (isAuthTeardownError(error)) return;
				enqueueSnackbar(i18n.t('admin.kbAdmin.errors.loadConfigs'), { variant: 'error' });
			}
		});

		buildReducers(builder, listKbFiles, 'listFilesRequest', {
			onFulfilled: (state, payload) => {
				state.files = payload.files;
				state.filesTotalCount = payload.totalCount;
				state.nextContinuationToken = payload.nextContinuationToken;
			},
			onRejected: (_state, error) => {
				if (isAuthTeardownError(error)) return;
				enqueueSnackbar(i18n.t('admin.kbAdmin.errors.loadFiles'), { variant: 'error' });
			}
		});

		buildReducers(builder, getPresignedUploadUrl, 'uploadFileRequest', {
			onRejected: () => {
				enqueueSnackbar(i18n.t('admin.kbAdmin.errors.upload'), { variant: 'error' });
			}
		});

		buildReducers(builder, uploadKbFile, 'uploadFileRequest', {
			onFulfilled: () => {
				enqueueSnackbar('File uploaded', { variant: 'success' });
			},
			onRejected: () => {
				enqueueSnackbar(i18n.t('admin.kbAdmin.errors.upload'), { variant: 'error' });
			}
		});

		buildReducers(builder, deleteKbFile, 'deleteFileRequest', {
			onFulfilled: (state, payload) => {
				state.files = state.files.filter((f: KbFile) => f.key !== payload.fileKey);
				state.filesTotalCount = Math.max(0, state.filesTotalCount - 1);
			},
			onRejected: () => {
				enqueueSnackbar(i18n.t('admin.kbAdmin.errors.delete'), { variant: 'error' });
			}
		});

		buildReducers(builder, triggerSync, 'triggerSyncRequest', {
			onFulfilled: (state, payload) => {
				state.latestSyncJob = payload;
				state.syncJobs = [payload, ...state.syncJobs];
				enqueueSnackbar(i18n.t('admin.kbAdmin.syncStarted'), { variant: 'success' });
			},
			onRejected: () => {
				enqueueSnackbar(i18n.t('admin.kbAdmin.errors.sync'), { variant: 'error' });
			}
		});

		buildReducers(builder, getSyncStatus, 'syncStatusRequest', {
			onFulfilled: (state, payload) => {
				state.syncJobs = payload.jobs;
				if (payload.jobs.length > 0) {
					state.latestSyncJob = payload.jobs[0];
				}
			}
		});

		// Config reducers
		buildReducers(builder, getKbConfig, 'getConfigRequest', {
			onFulfilled: (state, payload) => {
				state.selectedConfig = payload;
			},
			onRejected: (_state, error) => {
				if (isAuthTeardownError(error)) return;
				enqueueSnackbar(i18n.t('admin.kbAdmin.errors.loadConfig'), { variant: 'error' });
			}
		});

		buildReducers(builder, updateKbConfig, 'updateConfigRequest', {
			onFulfilled: (state, payload) => {
				state.selectedConfig = payload;
				state.configs = state.configs.map((c: KbConfig) =>
					c.knowledgeBaseId === payload.knowledgeBaseId ? payload : c
				);
				enqueueSnackbar('Configuration saved', { variant: 'success' });
			},
			onRejected: () => {
				enqueueSnackbar(i18n.t('admin.kbAdmin.errors.updateConfig'), { variant: 'error' });
			}
		});

		buildReducers(builder, updateKbConfigStatus, 'updateConfigStatusRequest', {
			onFulfilled: (state, payload) => {
				if (state.selectedConfig?.knowledgeBaseId === payload.knowledgeBaseId) {
					state.selectedConfig.status = payload.status;
				}
				state.configs = state.configs.map((c: KbConfig) =>
					c.knowledgeBaseId === payload.knowledgeBaseId ? { ...c, status: payload.status } : c
				);
			},
			onRejected: () => {
				enqueueSnackbar(i18n.t('admin.kbAdmin.errors.updateConfig'), { variant: 'error' });
			}
		});

		// Agent reducers
		buildReducers(builder, listAgents, 'listAgentsRequest', {
			onFulfilled: (state, payload) => {
				state.agents = payload.agents;
			},
			onRejected: (_state, error) => {
				if (isAuthTeardownError(error)) return;
				enqueueSnackbar(i18n.t('admin.kbAdmin.errors.loadAgents'), { variant: 'error' });
			}
		});

		buildReducers(builder, updateAgent, 'updateAgentRequest', {
			onFulfilled: (state, payload) => {
				state.agents = state.agents.map((a: AgentBinding) => (a.agentType === payload.agentType ? payload : a));
				enqueueSnackbar('Agent updated', { variant: 'success' });
			},
			onRejected: () => {
				enqueueSnackbar(i18n.t('admin.kbAdmin.errors.updateAgent'), { variant: 'error' });
			}
		});

		buildReducers(builder, updateAgentStatus, 'updateAgentStatusRequest', {
			onFulfilled: (state, payload) => {
				state.agents = state.agents.map((a: AgentBinding) =>
					a.agentType === payload.agentType ? { ...a, status: payload.status } : a
				);
			},
			onRejected: () => {
				enqueueSnackbar(i18n.t('admin.kbAdmin.errors.updateAgent'), { variant: 'error' });
			}
		});

		// Guardrail reducers
		buildReducers(builder, listGuardrails, 'listGuardrailsRequest', {
			onFulfilled: (state, payload) => {
				state.guardrails = payload.guardrails;
			},
			onRejected: (_state, error) => {
				if (isAuthTeardownError(error)) return;
				enqueueSnackbar('Failed to load guardrails', { variant: 'error' });
			}
		});

		buildReducers(builder, getGuardrailDetail, 'getGuardrailRequest', {
			onFulfilled: (state, payload) => {
				state.selectedGuardrail = payload;
			},
			onRejected: () => {
				enqueueSnackbar('Failed to load guardrail details', { variant: 'error' });
			}
		});

		// Allowed models reducers
		buildReducers(builder, listAllowedModels, 'listAllowedModelsRequest', {
			onFulfilled: (state, payload) => {
				state.allowedModels = payload.models;
			}
		});
	}
});

export const { resetKbAdmin, setSelectedKbId } = kbAdminSlice.actions;
export default kbAdminSlice.reducer;
